import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Trash2 } from "lucide-react";

import { formatCents, isRegressive, previewFee } from "@/lib/platform-fee";
import {
  useGetCarrierMarkup,
  useUpsertCarrierMarkup,
  useDeleteCarrierMarkup,
  useGetCarrierServiceMarkup,
  useUpsertCarrierServiceMarkup,
  useDeleteCarrierServiceMarkup,
  CarrierMarkupDeleteRequiresConfirmError,
  type MarkupInput,
} from "@/api/platform-fees";

/**
 * Phase 9: MarkupConfig tile — sits between scope card and route cards in
 * the unified pricing workspace. Two layers:
 *
 *   1. Carrier-wide default (PlatformFee, one row per Carrier).
 *      Applies to every CarrierService under this carrier unless overridden.
 *   2. Per-service override (PlatformServiceFee). Wins over the default.
 *
 * Always visible — markup applies to BOTH owned and API carriers (Decision 8
 * in the plan).
 */

interface FormState {
  percent: string; // string for input handling
  flatCents: string;
}

function emptyForm(): FormState {
  return { percent: "0", flatCents: "0" };
}

function fromMarkup(
  m: {
    percent: number;
    flatCents: number;
  } | null
): FormState {
  if (!m) return emptyForm();
  return {
    percent: String(m.percent),
    flatCents: (m.flatCents / 100).toFixed(2),
  };
}

function toInput(f: FormState): MarkupInput {
  const percent = Number(f.percent);
  const flat = Number(f.flatCents);
  if (
    !f.percent.trim() ||
    !f.flatCents.trim() ||
    !Number.isFinite(percent) ||
    percent < 0 ||
    !Number.isFinite(flat) ||
    flat < 0 ||
    !Number.isSafeInteger(Math.round(flat * 100))
  ) {
    throw new Error(
      "Enter a nonnegative percentage and flat fee, including an explicit zero."
    );
  }
  return { percent, flatCents: Math.round(flat * 100) };
}

/** Two forms are the same when the numbers a save would write are the same. */
const sameForm = (a: FormState, b: FormState) =>
  a.percent === b.percent && a.flatCents === b.flatCents;

/** What the workspace's single save bar needs from this section. */
export type MarkupConfigHandle = {
  /** True when the take rate on screen differs from what is stored. */
  isDirty: () => boolean;
  /**
   * Persist the staged take rate. Throws so the caller can report failure.
   *
   * ⚠️ Calls `onSaved` after EACH write that lands, because there are two: the
   * carrier-wide default and the per-service override. The carrier-wide one can
   * succeed and the override then fail — reporting "take rate" as a single unit
   * meant a landed write was announced as "Save failed", sending the user back to
   * re-enter a fee that is already stored.
   */
  save: (onSaved: (label: string) => void) => Promise<void>;
  /** Throw the staged take rate away and re-seed from the server. */
  reset: () => void;
};

export const MarkupConfig = forwardRef<
  MarkupConfigHandle,
  {
    carrierId: string;
    carrierName: string;
    carrierServiceId?: string;
    carrierServiceLabel?: string;
    /** How many active CarrierServices share this carrier (informational). */
    serviceCount?: number;
    /** Fires whenever staged-vs-stored changes, so the save bar can count it. */
    onDirtyChange?: (dirty: boolean) => void;
    /** Fires while this section is BUSY (writing, or still loading). */
    onSavingChange?: (saving: boolean) => void;
    /**
     * Fires when this section's data could not be READ.
     *
     * A failed markup query locks the workspace (an unlocked screen seeded from a
     * failed request would let a save write an empty fee over a real one) — so the
     * workspace has to be able to SAY so. A permanent lock with no explanation is
     * its own bug.
     */
    onLoadErrorChange?: (hasError: boolean) => void;
  }
>(function MarkupConfig(
  {
    carrierId,
    carrierName,
    carrierServiceId,
    carrierServiceLabel,
    serviceCount,
    onDirtyChange,
    onSavingChange,
    onLoadErrorChange,
  },
  ref
) {
  const carrierMarkupQuery = useGetCarrierMarkup(carrierId);
  const serviceMarkupQuery = useGetCarrierServiceMarkup(carrierServiceId);

  const [defaultForm, setDefaultForm] = useState<FormState>(emptyForm());
  /**
   * ⚠️ Has the user touched the carrier-wide fee?
   *
   * Needed because an explicit ZERO is a real, meaningful fee — and it is exactly
   * the one the screen was unable to save. With no fee stored, the baseline is an
   * empty form, so a user who types 0% / $0 produces a form IDENTICAL to the
   * baseline: nothing reads as dirty, no save bar appears, and the fee is never
   * created. Meanwhile the hint on that very form says the carrier CANNOT BE QUOTED
   * until a fee is saved, "even an explicit zero". The screen told you to do
   * something it would not let you do.
   *
   * The override has a checkbox to carry that intent. The default has nothing, so
   * the intent has to be tracked.
   */
  const [defaultTouched, setDefaultTouched] = useState(false);
  const [overrideForm, setOverrideForm] = useState<FormState>(emptyForm());
  const [overrideEnabled, setOverrideEnabled] = useState(false);

  // ⚠️ THE SCOPE ID IS PART OF THE KEY, AND LEAVING IT OUT WAS A CROSS-SCOPE WRITE.
  //
  // These effects keyed only on the query DATA. Switch between two carriers whose
  // markup happens to be equal — most commonly BOTH `null`, i.e. neither has a fee
  // — and `carrierMarkupQuery.data` never changes identity, so the effect never
  // re-fires and the PREVIOUS carrier's draft stays on screen. Meanwhile the
  // imperative handle closes over the NEW `carrierId`. Pressing save then writes
  // carrier A's numbers onto carrier B.
  //
  // Keying on the id makes switching scope always reseed, which is what "this form
  // belongs to that carrier" actually means.
  // ⚠️ Same rule as the rate tables: reseed on a SCOPE change, never clobber an
  // unsaved edit on a data refresh. A save invalidates these queries, so without
  // this guard anything typed between the click and the response is overwritten.
  //
  // The baselines are what we last SEEDED the forms from — comparing against the
  // live query instead would read a server refresh as a local edit.
  const lastCarrierId = useRef(carrierId);
  const lastServiceId = useRef(carrierServiceId);
  const defaultBaselineRef = useRef<FormState>(emptyForm());
  const overrideBaselineRef = useRef<FormState>(emptyForm());
  // ⚠️ SEEDED EXISTENCE, exactly like the seeded values.
  //
  // Reading existence off the live query meant a NEWLY CREATED override stayed
  // dirty after saving: `!!serviceMarkupQuery.data` is still false until the
  // refetch lands, so the bar kept offering to re-save a fee already stored.
  // Existence is part of the baseline, so it is baselined like everything else.
  const defaultExistsRef = useRef(false);
  const overrideExistsRef = useRef(false);

  const defaultDirty =
    defaultTouched &&
    (!defaultExistsRef.current ||
      !sameForm(defaultForm, defaultBaselineRef.current));

  // ⚠️ EXISTENCE IS PART OF THE VALUE.
  //
  // Ticking "override this service" and leaving it at 0% / $0 used to read as
  // clean — the form equalled the empty baseline — so it never saved. But an
  // explicit 0% override and NO override are different things: one says "this
  // service takes nothing", the other says "inherit whatever the carrier default
  // becomes". A screen that cannot express the first is a screen that silently
  // ignores you.
  // ⚠️ UNTICKING THE BOX IS A REAL CHANGE, AND IT USED TO BE A LIE.
  //
  // Unchecking "override this service" made the section read CLEAN — so the screen
  // said the service inherits the carrier-wide default while the override was still
  // sitting in the database, quietly pricing every shipment on that service.
  //
  // A checkbox that cannot be believed is worse than no checkbox. Unticking is now
  // a STAGED DELETION: the save bar lists it, Save removes the override, and
  // Discard puts it back. (The explicit Clear button still exists and still acts
  // immediately — that is for when you know you want it gone.)
  const overrideRemoved = !overrideEnabled && overrideExistsRef.current;
  const overrideDirty =
    overrideRemoved ||
    (overrideEnabled &&
      (!overrideExistsRef.current ||
        !sameForm(overrideForm, overrideBaselineRef.current)));

  useEffect(() => {
    const scopeChanged = lastCarrierId.current !== carrierId;
    lastCarrierId.current = carrierId;
    const baseline = fromMarkup(carrierMarkupQuery.data ?? null);
    // ⚠️ `defaultDirty`, not a fresh value comparison. A STAGED EXPLICIT ZERO has a
    // form identical to its baseline, so a value-only check reads "not edited",
    // reseeds, clears the intent, and silently throws the creation away. "Do not
    // clobber" and "there is something to save" must be the same question.
    if (!scopeChanged && defaultDirty) return;
    defaultBaselineRef.current = baseline;
    defaultExistsRef.current = !!carrierMarkupQuery.data;
    setDefaultForm(baseline);
    setDefaultTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultForm is read to
    // detect an edit; taking it as a dep would reseed on every keystroke.
  }, [carrierMarkupQuery.data, carrierId]);

  useEffect(() => {
    const scopeChanged = lastServiceId.current !== carrierServiceId;
    lastServiceId.current = carrierServiceId;
    const baseline = serviceMarkupQuery.data
      ? fromMarkup(serviceMarkupQuery.data)
      : emptyForm();
    // Same rule: a ticked checkbox with an all-zero form IS a staged creation, and
    // a value-only check would reseed it away (and untick the box).
    if (!scopeChanged && overrideDirty) return;
    overrideBaselineRef.current = baseline;
    overrideExistsRef.current = !!serviceMarkupQuery.data;
    setOverrideForm(baseline);
    setOverrideEnabled(!!serviceMarkupQuery.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [serviceMarkupQuery.data, carrierServiceId]);

  // Mutations
  // No per-mutation toasts: the save bar reports the whole save ONCE. Two toasts
  // for one click is how a user learns to stop reading them.
  const upsertCarrier = useUpsertCarrierMarkup({});
  const upsertService = useUpsertCarrierServiceMarkup({});

  // ── Staged, not saved ──────────────────────────────────────────────────────
  // The take rate is a VALUE EDIT, so it stages like the rate tables do and is
  // written by the workspace's one save bar. Compare against what the server
  // actually returned — that baseline is the only honest definition of "dirty".
  // An override that is switched OFF is a DELETION, and deletions are never
  // staged — see the Clear button. Only a live override's values are staged.
  const dirty = defaultDirty || overrideDirty;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // The save bar disables itself while ANY section is writing. If it did not know
  // about this one, a second click would land mid-flight and double-write.

  // DELETE confirmation flow
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState<{
    count: number;
    services: { id: string; code: string; name: string }[];
  } | null>(null);

  const deleteCarrier = useDeleteCarrierMarkup({
    onSuccess: () => {
      // ⚠️ Clear the DRAFT and its BASELINE, not just the server row.
      //
      // The reseed effect protects unsaved edits — so after a delete it would see
      // the old numbers still in the form, call them an unsaved edit, and refuse to
      // reseed. The next Save would then RECREATE the fee that was just deleted.
      defaultBaselineRef.current = emptyForm();
      defaultExistsRef.current = false;
      setDefaultForm(emptyForm());
      setDefaultTouched(false);
      toast.success("Removed", { description: "Carrier-wide markup cleared." });
      setConfirmOpen(false);
      setConfirmDetails(null);
    },
    onError: (e) => {
      if (e instanceof CarrierMarkupDeleteRequiresConfirmError) {
        setConfirmDetails({
          count: e.affectedServiceCount,
          services: e.affectedServices,
        });
        setConfirmOpen(true);
      } else {
        toast.error("Delete failed", { description: e.message });
      }
    },
  });
  const deleteService = useDeleteCarrierServiceMarkup({
    onSuccess: () => {
      // Same: a deleted override must leave no draft behind to be re-saved.
      overrideBaselineRef.current = emptyForm();
      overrideExistsRef.current = false;
      setOverrideForm(emptyForm());
      setOverrideEnabled(false);
      toast.success("Removed", {
        description: "Override cleared — falls back to carrier-wide default.",
      });
    },
    onError: (e) => toast.error("Delete failed", { description: e.message }),
  });

  /**
   * BUSY = writing OR still loading. Both mean "the state under this form is not
   * settled, so do not let anyone touch it".
   *
   *  - Writing: without the DELETES in here, an immediate Clear could race a "Save
   *    all changes" — one removing the fee while the other upserts it, with the
   *    winner decided by the network.
   *  - Loading: on first load the stored fee has not arrived. An edit made in that
   *    window marks the section dirty, which stops the reseed when the real fee
   *    lands — and a save then writes over it from an empty baseline.
   */
  const markupSaving =
    upsertCarrier.isPending ||
    upsertService.isPending ||
    deleteCarrier.isPending ||
    deleteService.isPending ||
    // `!isSuccess`, not `isLoading`: a FAILED query stops loading with no data, and
    // unlocking then would seed an empty fee and let a save write it over a real one.
    !carrierMarkupQuery.isSuccess ||
    (!!carrierServiceId && !serviceMarkupQuery.isSuccess);
  useEffect(() => {
    onSavingChange?.(markupSaving);
  }, [markupSaving, onSavingChange]);

  const markupLoadError =
    carrierMarkupQuery.isError ||
    (!!carrierServiceId && serviceMarkupQuery.isError);
  useEffect(() => {
    onLoadErrorChange?.(markupLoadError);
  }, [markupLoadError, onLoadErrorChange]);

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => dirty,
      reset: () => {
        // ⚠️ From the CURRENT server data, not the stale baseline refs.
        //
        // Discard means "the screen is the server again". If a refresh landed while
        // the edit was protected, the seeded refs are the state from BEFORE that
        // refresh — restoring them would put stale numbers back on screen and call
        // them clean.
        const nextDefault = fromMarkup(carrierMarkupQuery.data ?? null);
        const nextOverride = serviceMarkupQuery.data
          ? fromMarkup(serviceMarkupQuery.data)
          : emptyForm();
        defaultBaselineRef.current = nextDefault;
        overrideBaselineRef.current = nextOverride;
        defaultExistsRef.current = !!carrierMarkupQuery.data;
        overrideExistsRef.current = !!serviceMarkupQuery.data;
        setDefaultForm(nextDefault);
        setDefaultTouched(false);
        setOverrideForm(nextOverride);
        // Restore the checkbox too. Without this, Discard could leave the UI
        // saying "inherits the carrier-wide default" while a stored override is
        // still live on the server — the screen and the database disagreeing
        // about whether this service has its own fee.
        setOverrideEnabled(!!serviceMarkupQuery.data);
      },
      save: async (onSaved) => {
        // `mutateAsync` on purpose: the save bar must know if this failed, so it
        // can keep the section dirty rather than claim a save that never landed.
        if (defaultDirty) {
          await upsertCarrier.mutateAsync({
            carrierId,
            input: toInput(defaultForm),
          });
          // ⚠️ ADVANCE THE BASELINE. Without this the section stays dirty FOREVER:
          // the refetch that follows sees the saved form differing from the old
          // baseline, reads it as an unsaved edit, skips the reseed — and the save
          // bar keeps offering to save something that is already stored.
          defaultBaselineRef.current = defaultForm;
          defaultExistsRef.current = true;
          setDefaultTouched(false);
          onSaved("carrier-wide take rate");
        }
        if (carrierServiceId && overrideRemoved) {
          // The staged deletion.
          await deleteService.mutateAsync({ carrierServiceId });
          overrideBaselineRef.current = emptyForm();
          overrideExistsRef.current = false;
          setOverrideForm(emptyForm());
          onSaved("service override removed");
        } else if (carrierServiceId && overrideDirty) {
          await upsertService.mutateAsync({
            carrierServiceId,
            input: toInput(overrideForm),
          });
          overrideBaselineRef.current = overrideForm;
          overrideExistsRef.current = true;
          onSaved("service take-rate override");
        }
      },
    }),
    [
      dirty,
      defaultDirty,
      overrideDirty,
      overrideRemoved,
      deleteService,
      defaultForm,
      overrideForm,
      carrierMarkupQuery.data,
      serviceMarkupQuery.data,
      carrierId,
      carrierServiceId,
      upsertCarrier,
      upsertService,
    ]
  );

  function handleClearDefault() {
    deleteCarrier.mutate({ carrierId });
  }
  function handleConfirmDelete() {
    deleteCarrier.mutate({ carrierId, confirm: true });
  }
  function handleClearOverride() {
    if (carrierServiceId) deleteService.mutate({ carrierServiceId });
  }

  const hasDefault = !!carrierMarkupQuery.data;
  const carrierLoading = carrierMarkupQuery.isLoading;
  const serviceLoading = serviceMarkupQuery.isLoading;

  return (
    <Card>
      <CardHeader className="border-b px-3 pt-2.5 pb-1.5">
        <CardTitle className="text-sm">
          EZX take rate
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            applies on top of the carrier's label price — self-rated and
            API-procured carriers alike
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-3 py-3">
        {/* Carrier-wide default */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">
              Carrier-wide default
              <span className="ml-1 text-muted-foreground font-normal">
                ({carrierName} — applies to all {serviceCount}{" "}
                {serviceCount === 1 ? "service" : "services"} unless overridden)
              </span>
            </Label>
            {hasDefault && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleClearDefault}
                disabled={deleteCarrier.isPending}
                title="Clear carrier-wide default — services without an override fall back to no markup"
              >
                {deleteCarrier.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
              </Button>
            )}
          </div>
          {carrierLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading default…
            </div>
          ) : (
            <MarkupForm
              formId="markup-default"
              form={defaultForm}
              onChange={(f) => {
                setDefaultTouched(true);
                setDefaultForm(f);
              }}
              previewCarrierName={carrierName}
              hint={
                hasDefault
                  ? null
                  : "No fee configured — this carrier CANNOT BE QUOTED (PLATFORM_FEE_MISSING). Save a fee, even an explicit zero."
              }
            />
          )}
        </div>

        {/* Per-service override */}
        {carrierServiceId && (
          <div className="border-t pt-4 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={overrideEnabled}
                  onChange={(e) => setOverrideEnabled(e.target.checked)}
                  className="h-3 w-3"
                />
                Override for this service
                <span className="ml-1 text-muted-foreground font-normal">
                  ({carrierServiceLabel})
                </span>
              </Label>
              {overrideEnabled && serviceMarkupQuery.data && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={handleClearOverride}
                  disabled={deleteService.isPending}
                  title="Remove override — service falls back to carrier-wide default"
                >
                  {deleteService.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </Button>
              )}
            </div>
            {!overrideEnabled ? (
              <p className="text-[11px] text-muted-foreground">
                Service inherits the carrier-wide default. Tick the box above to
                override for this service only.
              </p>
            ) : serviceLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading override…
              </div>
            ) : (
              <MarkupForm
                formId="markup-override"
                form={overrideForm}
                onChange={setOverrideForm}
                previewCarrierName={carrierName}
                hint={null}
              />
            )}
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear carrier-wide fee?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {/*
                  This used to say the services would simply be sold with no
                  fee ("final price = label price"). That was true of the old
                  zero-fallback, which is gone: a service with no fee at either
                  level now FAILS its quote (PLATFORM_FEE_MISSING). Clearing
                  this takes those services off sale — say so.
                */}
                <p>
                  <strong>{confirmDetails?.count ?? 0} services</strong> have no
                  fee of their own and fall back to this one. Clearing it takes
                  them <strong>off sale</strong> — they cannot be quoted at all
                  until a new carrier-wide fee or a per-service fee is set.
                </p>
                {confirmDetails && confirmDetails.services.length > 0 && (
                  <div className="text-xs bg-muted rounded p-2 max-h-40 overflow-y-auto">
                    {confirmDetails.services.map((s) => (
                      <div key={s.id} className="font-mono">
                        {s.code}{" "}
                        <span className="text-muted-foreground">
                          — {s.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              {deleteCarrier.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Confirm and clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
});

/** Inline form for one markup row (default OR override). */
/**
 * ⚠️ NO SAVE BUTTON HERE — deliberately.
 *
 * This screen used to have TWO persistence models rendered with identical-looking
 * buttons: the take rate and dim factor wrote IMMEDIATELY on their own `Save`,
 * while the rate tables staged edits until "Save all rates" — which did NOT
 * include them. So you could change the take rate, press the big Save button, and
 * your change was not in it. Nothing on screen told you which button owned what.
 *
 * On a screen that sets money, that ambiguity is the bug. Now: one bar owns every
 * value edit on the page.
 *
 * Destructive actions (Clear / Remove) stay immediate and explicit — you should
 * never "stage" a deletion and forget you armed it.
 */
function MarkupForm({
  formId,
  form,
  onChange,
  previewCarrierName,
  hint,
}: {
  /** Distinguishes the carrier-wide form from the service override. */
  formId: string;
  form: FormState;
  onChange: (f: FormState) => void;
  previewCarrierName: string;
  hint: string | null;
}) {
  return (
    <div className="space-y-2">
      {/* No "type" selector any more. Percent and flat are two components of
          one fee, both always applied — they were never alternatives. The old
          enum forced a choice, so a second flat field got bolted on and a
          FIXED-cents "markup" became a duplicate of it. */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          {/* htmlFor/id: the Label was floating free, so the input had no
              accessible name — a screen reader (and a test) could not find it. */}
          <Label htmlFor={`${formId}-percent`} className="text-xs">
            Percentage of carrier rate (%)
          </Label>
          <Input
            id={`${formId}-percent`}
            type="number"
            step="0.1"
            min="0"
            value={form.percent}
            onChange={(e) => onChange({ ...form, percent: e.target.value })}
            className="num h-8 text-sm w-24 text-right"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${formId}-flat`} className="text-xs">
            Flat fee ($)
          </Label>
          <Input
            id={`${formId}-flat`}
            type="number"
            step="0.01"
            value={form.flatCents}
            onChange={(e) => onChange({ ...form, flatCents: e.target.value })}
            className="num h-8 text-sm w-24 text-right"
          />
        </div>
      </div>
      <FeePreview form={form} carrierName={previewCarrierName} hint={hint} />
    </div>
  );
}

/**
 * Show the consequence.
 *
 * A fee is two abstract numbers until you see what they DO. The regressive take
 * rate — ~20% of a $10 parcel but ~3% of a $60 one — was invisible for months
 * precisely because no screen ever showed the fee at more than one price. It
 * wasn't a decision anybody made; it was a decision nobody could see.
 */
function FeePreview({
  form,
  carrierName,
  hint,
}: {
  form: FormState;
  carrierName: string;
  hint: string | null;
}) {
  const fee = {
    percent: parseFloat(form.percent) || 0,
    flatCents: Math.round((parseFloat(form.flatCents) || 0) * 100),
  };
  const rows = previewFee(fee);
  const regressive = isRegressive(fee);
  const earnsNothing = fee.percent === 0 && fee.flatCents === 0;

  return (
    <div className="space-y-2 pt-1">
      {hint && <p className="text-[11px] text-destructive">{hint}</p>}

      <div className="overflow-hidden rounded-md border">
        <p className="border-b px-3 py-1.5 text-[11px] font-medium text-muted-foreground">
          What EZX keeps on a {carrierName} shipment
        </p>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-1.5 text-left font-medium">
                Carrier&apos;s rate
              </th>
              <th className="px-3 py-1.5 text-right font-medium">
                Customer pays
              </th>
              <th className="px-3 py-1.5 text-right font-medium">EZX keeps</th>
              <th className="px-3 py-1.5 text-right font-medium">Take</th>
            </tr>
          </thead>
          <tbody className="num">
            {rows.map((r) => (
              <tr key={r.carrierRateCents} className="border-b last:border-0">
                <td className="px-3 py-1">{formatCents(r.carrierRateCents)}</td>
                <td className="px-3 py-1 text-right">
                  {formatCents(r.carrierRateCents + r.feeCents)}
                </td>
                <td className="px-3 py-1 text-right font-medium">
                  {formatCents(r.feeCents)}
                </td>
                <td
                  className={
                    "px-3 py-1 text-right font-semibold " +
                    (r.takePercent < 6 ? "text-bad" : "text-ok")
                  }
                >
                  {r.takePercent.toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {earnsNothing && (
        <p className="text-[11px] text-muted-foreground">
          <span className="font-semibold text-bad">
            This carrier earns EZX nothing.
          </span>{" "}
          That is allowed — but it has to be a decision, not an oversight.
        </p>
      )}

      {regressive && (
        <p className="text-[11px] text-destructive">
          Flat fee only: the take collapses as parcels get more expensive (
          {rows[0].takePercent.toFixed(0)}% on a{" "}
          {formatCents(rows[0].carrierRateCents)} parcel,{" "}
          {rows[rows.length - 1].takePercent.toFixed(1)}% on a{" "}
          {formatCents(rows[rows.length - 1].carrierRateCents)} one) — and the
          expensive parcel carries the most risk. Add a percentage.
        </p>
      )}
    </div>
  );
}
